﻿import flatpickr from 'flatpickr';
import monthSelectPlugin from 'flatpickr/dist/plugins/monthSelect/index.js';
import 'flatpickr/dist/flatpickr.min.css';
import 'flatpickr/dist/plugins/monthSelect/style.css';
import { French } from 'flatpickr/dist/l10n/fr.js';
import { parseFlatpickrDate } from './utils.js';
import { showToast } from './toast.js';

const FrenchCapitalized = {
    ...French,
    months: {
        shorthand: French.months.shorthand.map(m => m.charAt(0).toUpperCase() + m.slice(1)),
        longhand: French.months.longhand.map(m => m.charAt(0).toUpperCase() + m.slice(1)),
    }
}

function initUpsertDatepicker() {

    const picker = document.querySelector('[data-js-field="accountStartDate"]');
    if (!picker) return;

    const isEditMode = document.querySelector('.pageTitle')?.dataset.pageMode === 'True';
    if (isEditMode) {
        picker.setAttribute("disabled", "disabled");
        return;
    }

    picker.removeAttribute("disabled");

    const today = new Date();

    flatpickr(picker, {
        locale: FrenchCapitalized,
        allowInput: false,
        maxDate: today,
        defaultDate: today,
        plugins: [new monthSelectPlugin({ shorthand: false })],
    });
}

function initEventCreationDatepicker(onMonthChange) {

    const picker = document.querySelector('[data-js-field="eventCreationDate"]');
    if (!picker) return;

    const accountStartDate = document.querySelector('#currentAccountStartDate')?.value;
    if (!accountStartDate) return;
    const parsedMinDate = parseFlatpickrDate(accountStartDate);
    const today = new Date();
    const maxDate = new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59, 999);

    flatpickr(picker, {
        locale: FrenchCapitalized,
        dateFormat: 'd/m/Y',
        allowInput: false,
        maxDate: maxDate,
        minDate: parsedMinDate,
        defaultDate: today,
        position: "auto left",
        onChange: function (selectedDates) {
            if (selectedDates[0] && onMonthChange) {
                onMonthChange(selectedDates[0]);
            }
        },
        onMonthChange: function (selectedDates, dateStr, instance) {
            const newDate = getTargetDate(instance);
            instance.setDate(newDate, true);
        },
        onYearChange: function (selectedDates, dateStr, instance) {
            const newDate = getTargetDate(instance);
            instance.setDate(newDate, true);
        }
    })
}

function getTargetDate(instance) {
    const previousDate = instance.latestSelectedDateObj;
    if (!previousDate) return new Date();
    const previousMonth = previousDate?.getMonth();
    const previousDay = previousDate?.getDate();
    const previousYear = previousDate?.getFullYear();
    const wasLastDayInMonth = previousDay === new Date(previousYear, previousMonth + 1, 0).getDate();
    if (wasLastDayInMonth) return new Date(instance.currentYear, instance.currentMonth + 1, 0)
    return new Date(instance.currentYear, instance.currentMonth, previousDay);
}

function initEventEditDatepicker(minDate, maxDate) {

    const picker = document.querySelector('[data-js-field="eventUpdateDate"]');

    if (!picker) return;

    flatpickr(picker, {
        locale: French,
        dateFormat: 'd/m/Y',
        allowInput: false,
        maxDate: maxDate,
        minDate: minDate
    })
}

function initPdfDateRangePickers() {

    const startPicker = document.querySelector('[data-js-field="periodStartDate"]');
    const endPicker = document.querySelector('[data-js-field="periodEndDate"]');
    let startInstance;
    let endInstance;
    if (!startPicker || !endPicker) return;

    const minDate = parseFlatpickrDate(startPicker.dataset.minDate);
    const today = new Date();
    const defaultDate = new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59, 999);

    let beforeChangeStartDate = defaultDate;
    let beforeChangeEndDate = defaultDate;

    endInstance = flatpickr(endPicker, {
        locale: FrenchCapitalized,
        allowInput: false,
        plugins: [new monthSelectPlugin({ shorthand: false, dateFormat: 'F Y' })],
        minDate: minDate,
        maxDate: defaultDate,
        defaultDate: defaultDate,
        onChange: (selectedDates, dateStr, instance) => {
            if (startInstance.selectedDates[0] && isStartDatePosteriorToEndDate(startInstance.selectedDates[0], selectedDates[0])) {
                instance.setDate(beforeChangeEndDate, false);
                showToast('error', "La date de fin ne peut être antérieure à la date de début.")
                return;
            }
            beforeChangeEndDate = selectedDates[0];
        }
    });

    startInstance = flatpickr(startPicker, {
        locale: FrenchCapitalized,
        allowInput: false,
        plugins: [new monthSelectPlugin({ shorthand: false, dateFormat: 'F Y' })],
        minDate: minDate,
        maxDate: defaultDate,
        defaultDate: defaultDate,
        onChange: (selectedDates, dateStr, instance) => {
            if (endInstance.selectedDates[0] && isStartDatePosteriorToEndDate(selectedDates[0], endInstance.selectedDates[0])) {
                instance.setDate(beforeChangeStartDate, false);
                showToast('error', "La date de début ne peut être postérieure à la date de fin.")
                return;
            }
            beforeChangeStartDate = selectedDates[0];
        }
    });
}

function isStartDatePosteriorToEndDate(startDate, endDate) {
    if (startDate.getFullYear() !== endDate.getFullYear())
        return startDate.getFullYear() > endDate.getFullYear();
    return startDate.getMonth() > endDate.getMonth();
}

export {
    initUpsertDatepicker,
    initEventCreationDatepicker,
    initEventEditDatepicker,
    initPdfDateRangePickers
}